import { parseUpdateTags } from "../extensions/mintlify/update-html"
import { escapePanelAttribute, indentPanelBlock } from "../panel-markdown"

interface TurndownRule {
	filter: (node: Node) => boolean
	replacement: (content: string, node: Node) => string
}

interface TurndownLike {
	addRule: (key: string, rule: TurndownRule) => void
}

const typeOf = (node: Node): string | null =>
	node.nodeType === 1 ? (node as HTMLElement).getAttribute("data-type") : null

const hasAttribute = (node: Node, name: string): boolean =>
	node.nodeType === 1 && (node as HTMLElement).hasAttribute(name)

// Label, description and tags travel as Mintlify's props; only the body is markdown.
export function addUpdateMarkdownRules(turndown: TurndownLike): void {
	turndown.addRule("mintlifyUpdate", {
		filter: (node) => node.nodeName === "DIV" && typeOf(node) === "update",
		replacement: (content, node) => {
			const element = node as HTMLElement
			const text = (selector: string) =>
				element.querySelector(selector)?.textContent?.trim() ?? ""
			const label = text("[data-update-label]")
			const description = text("[data-update-description]")
			const tags = parseUpdateTags(element.getAttribute("data-tags"))
			const props = [
				`label="${escapePanelAttribute(label)}"`,
				description ? `description="${escapePanelAttribute(description)}"` : "",
				tags.length > 0
					? `tags={[${tags.map((tag) => JSON.stringify(tag)).join(", ")}]}`
					: "",
			]
				.filter(Boolean)
				.join(" ")
			return `\n\n<Update ${props}>\n${indentPanelBlock(content.trim())}\n</Update>\n\n`
		},
	})

	turndown.addRule("mintlifyUpdateMeta", {
		filter: (node) => node.nodeName === "DIV" && typeOf(node) === "update-meta",
		replacement: () => "",
	})

	turndown.addRule("mintlifyUpdateLines", {
		filter: (node) =>
			node.nodeName === "P" &&
			(hasAttribute(node, "data-update-label") ||
				hasAttribute(node, "data-update-description")),
		replacement: () => "",
	})

	turndown.addRule("mintlifyUpdateContent", {
		filter: (node) =>
			node.nodeName === "DIV" && typeOf(node) === "update-content",
		replacement: (content) => `\n\n${content.trim()}\n\n`,
	})
}
